import React, { useEffect, useState, useCallback } from 'react';
import {
  getSportsLeagues,
  getSportsEvents,
} from '../../services/contentPlugins';
import { buildSportsPreviewHtml } from '../../utils/pluginPreviewHtml';
import { getSportsScoreboardDates } from '../../utils/sportsScoreboardDate';
import PluginRenderingBar from './PluginRenderingBar';
import './ContentPlugins.css';

const RENDERING_MESSAGE = 'Generating your plugin image… You can close this window — we\'ll keep working in the background.';

async function fetchMergedSportsEvents(sportKey, token) {
  const dates = getSportsScoreboardDates();
  const responses = await Promise.all(
    dates.map((date) => getSportsEvents(sportKey, token, date)),
  );
  const seen = new Set();
  const merged = [];
  for (const res of responses) {
    for (const ev of res.data.events || []) {
      if (seen.has(ev.eventId)) continue;
      seen.add(ev.eventId);
      merged.push(ev);
    }
  }
  const order = { in: 0, pre: 1, post: 2 };
  merged.sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
  return merged;
}

export default function SportsPluginConfig({
  open,
  onClose,
  onSave,
  userId,
  screenId,
  token,
  initialConfig,
  saving,
  rendering = false,
}) {
  const [leagues, setLeagues] = useState([]);
  const [activeSport, setActiveSport] = useState(null);
  const [events, setEvents] = useState([]);
  const [selections, setSelections] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (open) {
      if (!rendering) {
        getSportsLeagues(token)
          .then((res) => setLeagues(res.data.leagues || []))
          .catch(() => setLeagues([]));
      }
      if (initialConfig?.renderConfig?.selections?.length) {
        setSelections(initialConfig.renderConfig.selections);
      } else if (initialConfig?.selections?.length) {
        setSelections(initialConfig.selections);
      }
    }
  }, [open, token, initialConfig, rendering]);

  useEffect(() => {
    if (!activeSport || rendering) {
      if (!activeSport) setEvents([]);
      return;
    }
    setLoadingEvents(true);
    fetchMergedSportsEvents(activeSport, token)
      .then((events) => setEvents(events))
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [activeSport, token]);

  const buildConfig = useCallback(() => ({
    selections,
    layout: 'auto',
  }), [selections]);

  const loadPreview = useCallback(() => {
    setPreviewHtml(buildSportsPreviewHtml({ selections, layout: 'auto' }));
  }, [selections]);

  useEffect(() => {
    if (!open) return undefined;
    loadPreview();
    return undefined;
  }, [open, loadPreview]);

  const toggleGame = (event) => {
    if (rendering) return;
    const key = `${event.sportKey}:${event.eventId}`;
    const existing = selections.find(
      (s) => s.type === 'games' && s.sportKey === event.sportKey && s.eventIds?.includes(event.eventId),
    );
    if (existing) {
      setSelections((prev) => prev.map((s) => {
        if (s.type !== 'games' || s.sportKey !== event.sportKey) return s;
        const ids = (s.eventIds || []).filter((id) => id !== event.eventId);
        return ids.length ? { ...s, eventIds: ids } : null;
      }).filter(Boolean));
      return;
    }
    const gameSel = selections.find((s) => s.type === 'games' && s.sportKey === event.sportKey);
    if (gameSel) {
      setSelections((prev) => {
        const withoutDynamic = prev.filter(
          (s) => !(s.type === 'top' || s.type === 'sport') || s.sportKey !== event.sportKey,
        );
        return withoutDynamic.map((s) => (
          s.type === 'games' && s.sportKey === event.sportKey
            ? { ...s, eventIds: [...(s.eventIds || []), event.eventId] }
            : s
        ));
      });
    } else {
      setSelections((prev) => {
        const withoutDynamic = prev.filter(
          (s) => !(s.type === 'top' || s.type === 'sport') || s.sportKey !== event.sportKey,
        );
        return [...withoutDynamic, { type: 'games', sportKey: event.sportKey, eventIds: [event.eventId] }];
      });
    }
    void key;
  };

  const isGameSelected = (event) => selections.some(
    (s) => s.type === 'games' && s.sportKey === event.sportKey && s.eventIds?.includes(event.eventId),
  );

  const addTopGames = (sportKey, limit = 4) => {
    if (rendering) return;
    setSelections((prev) => {
      const filtered = prev.filter(
        (s) => s.sportKey !== sportKey || (s.type !== 'top' && s.type !== 'games' && s.type !== 'sport'),
      );
      return [...filtered, { type: 'top', sportKey, limit }];
    });
  };

  const addAllSport = (sportKey) => {
    if (rendering) return;
    setSelections((prev) => {
      const filtered = prev.filter(
        (s) => s.sportKey !== sportKey || (s.type !== 'sport' && s.type !== 'games' && s.type !== 'top'),
      );
      return [...filtered, { type: 'sport', sportKey }];
    });
  };

  const removeSelection = (idx) => {
    if (rendering) return;
    setSelections((prev) => prev.filter((_, i) => i !== idx));
  };

  const selectionLabel = (sel) => {
    const league = leagues.find((l) => l.sportKey === sel.sportKey);
    const name = league?.name || sel.sportKey;
    if (sel.type === 'top') return `${name} — Top ${sel.limit} games`;
    if (sel.type === 'sport') return `${name} — All games today`;
    if (sel.type === 'games') return `${name} — ${sel.eventIds?.length || 0} selected games`;
    return name;
  };

  if (!open) return null;

  const readOnly = rendering;

  return (
    <div className="contentPluginOverlay" role="presentation">
      <div
        className={`contentPluginConfigModal contentPluginConfigModalWide ${readOnly ? 'contentPluginConfigReadOnly' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="contentPluginConfigHeader">
          <h2>Sports Scoreboard</h2>
          <button type="button" className="contentPluginClose" onClick={onClose}>×</button>
        </div>
        {rendering ? (
          <PluginRenderingBar message={RENDERING_MESSAGE} />
        ) : null}
        <div className="contentPluginConfigBody">
          <div className="contentPluginForm">
            <p className="contentPluginSectionLabel">Sports</p>
            <div className="contentPluginChips">
              {leagues.map((l) => (
                <button
                  key={l.sportKey}
                  type="button"
                  className={`contentPluginChip ${activeSport === l.sportKey ? 'active' : ''}`}
                  onClick={() => !readOnly && setActiveSport(l.sportKey)}
                  disabled={readOnly}
                >
                  {l.emoji} {l.name}
                </button>
              ))}
            </div>
            {activeSport && !readOnly && (
              <div className="contentPluginSportActions">
                <button type="button" onClick={() => addTopGames(activeSport, 4)}>Top 4 games</button>
                <button type="button" onClick={() => addAllSport(activeSport)}>All today&apos;s games</button>
              </div>
            )}
            {loadingEvents && !readOnly && <p className="contentPluginHint">Loading games...</p>}
            {events.length > 0 && !readOnly && (
              <ul className="contentPluginGameList">
                {events.map((ev) => (
                  <li key={ev.eventId}>
                    <button
                      type="button"
                      className={`contentPluginGameItem ${isGameSelected(ev) ? 'selected' : ''}`}
                      onClick={() => toggleGame(ev)}
                      disabled={readOnly}
                    >
                      <span>{ev.label}</span>
                      <span className="contentPluginGameStatus">{ev.statusLabel}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="contentPluginSectionLabel">My Scoreboard</p>
            {selections.length === 0 ? (
              <p className="contentPluginHint">Pick games or add a sport above</p>
            ) : (
              <ul className="contentPluginSelectionList">
                {selections.map((sel, idx) => (
                  <li key={`${sel.type}-${sel.sportKey}-${idx}`}>
                    <span>{selectionLabel(sel)}</span>
                    <button type="button" onClick={() => removeSelection(idx)} disabled={readOnly}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="contentPluginPreviewPane">
            <p className="contentPluginPreviewLabel">Preview</p>
            <div className="contentPluginPreviewFrameWrap">
              {previewHtml ? (
                <iframe
                  title="Sports preview"
                  className="contentPluginPreviewFrame"
                  srcDoc={previewHtml}
                  sandbox="allow-scripts"
                />
              ) : (
                <div className="contentPluginPreviewEmpty">Loading preview…</div>
              )}
            </div>
          </div>
        </div>
        <div className={`contentPluginConfigFooter ${rendering ? 'contentPluginConfigFooterRendering' : ''}`}>
          {rendering ? (
            <p className="contentPluginRenderingHint">
              Reopen this slot anytime to check progress. Editing unlocks when the video is ready.
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" className="contentPluginCancel" onClick={onClose}>
              {rendering ? 'Close' : 'Cancel'}
            </button>
            {!rendering ? (
              <button
                type="button"
                className="contentPluginSave"
                disabled={!selections.length || saving}
                onClick={() => onSave(buildConfig())}
              >
                {saving ? 'Starting…' : 'Save Sports Plugin'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
